import { useEffect, useReducer, useState, useContext } from 'react';
import toastError from '../../toasts/toastError';
import { toast } from 'react-toastify';

import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';

import { DeleteOutline, Edit } from '@mui/icons-material';

import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import TableRowSkeleton from '../../components/TableRowSkeleton';
import Title from '../../components/Title';
import QueueModal from '../../components/QueueModal';
import ConfirmationModal from '../../components/ConfirmationModal';
import ForbiddenPage from '../../components/ForbiddenPage';

import { i18n } from '../../translate/i18n';
import api from '../../services/api';
import { AuthContext } from '../../context/Auth/AuthContext';
import { getStyled } from './styles';

const reducer = (state, action) => {
  if (action.type === 'LOAD_QUEUES') {
    const queues = action.payload;
    const newQueues = [];

    queues.forEach((queue) => {
      const queueIndex = state.findIndex((q) => q.id === queue.id);
      if (queueIndex !== -1) {
        state[queueIndex] = queue;
      } else {
        newQueues.push(queue);
      }
    });

    return [...state, ...newQueues];
  }

  if (action.type === 'UPDATE_QUEUES') {
    const queue = action.payload;
    const queueIndex = state.findIndex((u) => u.id === queue.id);

    if (queueIndex !== -1) {
      state[queueIndex] = queue;
      return [...state];
    } else {
      return [queue, ...state];
    }
  }

  if (action.type === 'DELETE_QUEUE') {
    const queueId = action.payload;
    const queueIndex = state.findIndex((q) => q.id === queueId);
    if (queueIndex !== -1) {
      state.splice(queueIndex, 1);
    }
    return [...state];
  }

  if (action.type === 'RESET') {
    return [];
  }
};

const Queues = () => {
  const theme = useTheme();
  const classes = getStyled(theme);

  const [queues, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);

  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const { user, socket } = useContext(AuthContext);
  const companyId = user.companyId;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/queue');
        dispatch({ type: 'LOAD_QUEUES', payload: data });

        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const onQueueEvent = (data) => {
      if (data.action === 'update' || data.action === 'create') {
        dispatch({ type: 'UPDATE_QUEUES', payload: data.queue });
      }

      if (data.action === 'delete') {
        dispatch({ type: 'DELETE_QUEUE', payload: data.queueId });
      }
    };
    socket.on(`company-${companyId}-queue`, onQueueEvent);

    return () => {
      socket.off(`company-${companyId}-queue`, onQueueEvent);
    };
  }, [socket, companyId]);

  const handleOpenQueueModal = () => {
    setQueueModalOpen(true);
    setSelectedQueue(null);
  };

  const handleCloseQueueModal = () => {
    setQueueModalOpen(false);
    setSelectedQueue(null);
  };

  const handleEditQueue = (queue) => {
    setSelectedQueue(queue);
    setQueueModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedQueue(null);
  };

  const handleDeleteQueue = async (queueId) => {
    try {
      await api.delete(`/queue/${queueId}`);
      toastSuccess(`${i18n.t('queues.toasts.deleted')}`);
    } catch (err) {
      toastError(err);
    }
    setSelectedQueue(null);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          selectedQueue &&
          `${i18n.t('queues.confirmationModal.deleteTitle')} ${
            selectedQueue.name
          }?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteQueue(selectedQueue.id)}
      >
        {i18n.t('queues.confirmationModal.deleteMessage')}
      </ConfirmationModal>
      <QueueModal
        open={queueModalOpen}
        onClose={handleCloseQueueModal}
        queueId={selectedQueue?.id}
        onEdit={(res) => {
          if (res) {
            setTimeout(() => {
              handleEditQueue(res);
            }, 500);
          }
        }}
      />
      {user.profile === 'user' ? (
        <ForbiddenPage />
      ) : (
        <>
          <MainHeader>
            <Title>
              {i18n.t('queues.title')} ({queues.length})
            </Title>
            <MainHeaderButtonsWrapper>
              <Button
                variant='outlined'
                sx={theme.custom.editButton}
                onClick={handleOpenQueueModal}
              >
                {i18n.t('queues.buttons.add')}
              </Button>
            </MainHeaderButtonsWrapper>
          </MainHeader>
          <Paper sx={classes.mainPaper} variant='outlined'>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell align='center'>
                    {i18n.t('queues.table.ID')}
                  </TableCell>
                  <TableCell align='center'>
                    {i18n.t('queues.table.name')}
                  </TableCell>
                  <TableCell align='center'>
                    {i18n.t('queues.table.color')}
                  </TableCell>
                  <TableCell align='center'>
                    {i18n.t('queues.table.greeting')}
                  </TableCell>
                  <TableCell align='center'>
                    {i18n.t('queues.table.actions')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <>
                  {queues
                    .sort((a, b) => a.id - b.id)
                    .map((queue) => (
                      <TableRow key={queue.id}>
                        <TableCell align='center'>{queue.id}</TableCell>
                        <TableCell align='center'>{queue.name}</TableCell>
                        <TableCell align='center'>
                          <div style={classes.customTableCell}>
                            <span
                              style={{
                                backgroundColor: queue.color,
                                width: 60,
                                height: 20,
                                alignSelf: 'center',
                                borderRadius: 3,
                              }}
                            />
                          </div>
                        </TableCell>
                        <TableCell align='center'>
                          <div style={classes.customTableCell}>
                            <Typography
                              sx={{ width: 300, align: 'center' }}
                              noWrap
                              variant='body2'
                            >
                              {queue.greetingMessage}
                            </Typography>
                          </div>
                        </TableCell>
                        <TableCell align='center'>
                          <IconButton
                            size='small'
                            onClick={() => handleEditQueue(queue)}
                          >
                            <Edit />
                          </IconButton>

                          <IconButton
                            size='small'
                            onClick={() => {
                              setSelectedQueue(queue);
                              setConfirmModalOpen(true);
                            }}
                          >
                            <DeleteOutline
                              sx={{ color: theme.custom.colors.cancelButton }}
                            />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  {loading && <TableRowSkeleton columns={4} />}
                </>
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </MainContainer>
  );
};

export default Queues;
